// ============================================================================
// FlowTestTab -- simulation engine + chat UI for the unified flows page
// Extracted from routes/_auth.operator/flow-tester.tsx
// ============================================================================

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useOperator } from '@/lib/operator-context';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Play,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Globe,
  Eye,
  MessageSquare,
  Send,
  BarChart3,
  ArrowDown,
  Circle,
  CircleDot,
  GitBranch,
  Zap,
  Square,
  Info,
  ScrollText,
  CheckCircle2,
  Bot,
  User,
} from 'lucide-react';
import { getFlow, getFlowFunnel } from '@/lib/operator-api/flows-client';
import type { FlowNode, FlowDetail, FunnelStage } from '@/lib/operator-api/flows-client';
import type { FlowTabProps } from './types';

// ---------------------------------------------------------------------------
// Local types
// ---------------------------------------------------------------------------

interface SimLogEntry {
  timestamp: Date;
  type: 'bot' | 'user' | 'system';
  nodeSlug: string | null;
  nodeName: string | null;
  message: string;
}

interface ChatMessage {
  id: string;
  sender: 'bot' | 'user';
  text: string;
  timestamp: Date;
  nodeSlug?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const NODE_TYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  start: Play,
  interaction: MessageSquare,
  decision: GitBranch,
  action: Zap,
  terminal: Square,
};

const NODE_TYPE_COLORS: Record<string, string> = {
  start: 'bg-emerald-500',
  interaction: 'bg-blue-500',
  decision: 'bg-amber-500',
  action: 'bg-violet-500',
  terminal: 'bg-slate-500',
};

const NODE_TYPE_RING_COLORS: Record<string, string> = {
  start: 'ring-emerald-500/30',
  interaction: 'ring-blue-500/30',
  decision: 'ring-amber-500/30',
  action: 'ring-violet-500/30',
  terminal: 'ring-slate-500/30',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function NodeTypeIcon({ nodeType, className }: { nodeType: string; className?: string }) {
  const IconComponent = NODE_TYPE_ICONS[nodeType] || Circle;
  return <IconComponent className={className} />;
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

// ---------------------------------------------------------------------------
// FlowVisualizer (vertical timeline)
// ---------------------------------------------------------------------------

function FlowVisualizer({
  nodes,
  currentIndex,
  inspectedNode,
  locale,
  onNodeClick,
}: {
  nodes: FlowNode[];
  currentIndex: number;
  inspectedNode: FlowNode | null;
  locale: 'en' | 'pt-BR';
  onNodeClick: (node: FlowNode) => void;
}) {
  const { t } = useTranslation();
  const activeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [currentIndex]);

  if (nodes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <Eye className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">{t('flowTester.selectFlowToVisualize')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-0 py-2">
      {nodes.map((node, idx) => {
        const isCurrent = idx === currentIndex;
        const isPast = idx < currentIndex;
        const isInspected = inspectedNode?.id === node.id;
        const color = NODE_TYPE_COLORS[node.nodeType] || 'bg-gray-400';
        const ringColor = NODE_TYPE_RING_COLORS[node.nodeType] || 'ring-gray-400/30';
        const nodeMessage =
          locale === 'pt-BR' && node.messageTextPtBr
            ? node.messageTextPtBr
            : node.messageText;
        const nodeName = locale === 'pt-BR' && node.namePtBr ? node.namePtBr : node.name;

        return (
          <div key={node.id}>
            <div
              ref={isCurrent ? activeRef : undefined}
              onClick={() => onNodeClick(node)}
              className={`relative flex items-start gap-4 px-4 py-3 rounded-xl cursor-pointer transition-all duration-200 group ${
                isCurrent
                  ? 'bg-[hsl(160,84%,39%)]/10 border-2 border-[hsl(160,84%,39%)]/30 shadow-sm'
                  : isInspected
                    ? 'bg-blue-50 border-2 border-blue-300'
                    : 'hover:bg-muted/50 border-2 border-transparent'
              }`}
            >
              {/* Timeline dot */}
              <div className="flex flex-col items-center flex-shrink-0 pt-0.5">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-white ${color} ${
                    isCurrent ? `ring-4 ${ringColor}` : ''
                  } ${isPast ? 'opacity-60' : ''}`}
                >
                  <NodeTypeIcon nodeType={node.nodeType} className="h-4 w-4" />
                </div>
              </div>

              {/* Node content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`font-medium text-sm ${isPast ? 'text-muted-foreground' : 'text-foreground'}`}>
                    {nodeName}
                  </span>
                  <Badge
                    variant="outline"
                    className={`text-[10px] px-1.5 py-0 capitalize ${
                      isCurrent ? 'border-[hsl(160,84%,39%)]/40 text-[hsl(160,60%,18%)]' : ''
                    }`}
                  >
                    {node.nodeType}
                  </Badge>
                  {isCurrent && (
                    <CircleDot className="h-3.5 w-3.5 text-[hsl(160,84%,39%)] animate-pulse" />
                  )}
                  {isPast && (
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  )}
                </div>
                {nodeMessage && (
                  <p className={`text-xs mt-1 line-clamp-2 ${isPast ? 'text-muted-foreground/60' : 'text-muted-foreground'}`}>
                    {nodeMessage}
                  </p>
                )}
                {node.templateSlug && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono text-violet-600 dark:text-violet-400 mt-1">
                    {t('flowTester.usesTemplate', 'Uses template')}: {node.templateSlug}
                  </span>
                )}
              </div>

              {/* Step number */}
              <div className="flex-shrink-0 text-xs text-muted-foreground font-mono pt-1">
                #{idx + 1}
              </div>
            </div>

            {/* Connector line between nodes */}
            {idx < nodes.length - 1 && (
              <div className="flex justify-start pl-[1.95rem] py-0">
                <div className={`w-0.5 h-4 ${isPast ? 'bg-emerald-300' : isCurrent ? 'bg-[hsl(160,84%,39%)]/40' : 'bg-border'}`} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ChatSimulator
// ---------------------------------------------------------------------------

function ChatSimulator({
  messages,
  onSendMessage,
  onNextStep,
  onPrevStep,
  onReset,
  isAtEnd,
  isAtStart,
  locale,
  businessName,
}: {
  messages: ChatMessage[];
  onSendMessage: (text: string) => void;
  onNextStep: () => void;
  onPrevStep: () => void;
  onReset: () => void;
  isAtEnd: boolean;
  isAtStart: boolean;
  locale: 'en' | 'pt-BR';
  businessName: string;
}) {
  const { t } = useTranslation();
  const [inputText, setInputText] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = useCallback(() => {
    const trimmed = inputText.trim();
    if (!trimmed) return;
    onSendMessage(trimmed);
    setInputText('');
  }, [inputText, onSendMessage]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  return (
    <div className="flex flex-col h-full">
      {/* Chat header -- WhatsApp green */}
      <div className="flex items-center gap-3 px-4 py-3 bg-[#075e54] text-white rounded-t-xl">
        <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
          <Bot className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{businessName}</p>
          <p className="text-[11px] text-white/70">{t('flowTester.testMode')}</p>
        </div>
        <Badge className="bg-amber-500/80 text-white border-0 text-[10px]">
          {t('flowTester.simulator')}
        </Badge>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-[#ece5dd] min-h-[280px] max-h-[400px]">
        {messages.length === 0 && (
          <div className="flex items-center justify-center h-full text-sm text-[#075e54]/50">
            {t('flowTester.startSimulation')}
          </div>
        )}
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] px-3 py-2 rounded-lg text-sm shadow-sm ${
                msg.sender === 'user'
                  ? 'bg-[#dcf8c6] text-gray-900 rounded-tr-none'
                  : 'bg-white text-gray-900 rounded-tl-none'
              }`}
            >
              <p className="whitespace-pre-wrap">{msg.text}</p>
              <p
                className={`text-[10px] mt-1 text-right ${
                  msg.sender === 'user' ? 'text-gray-500' : 'text-gray-400'
                }`}
              >
                {formatTime(msg.timestamp)}
              </p>
            </div>
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>

      {/* Navigation controls */}
      <div className="flex items-center justify-between px-3 py-2 bg-[#f0ebe3] border-t border-[#d1c7b7]">
        <Button
          variant="ghost"
          size="sm"
          onClick={onPrevStep}
          disabled={isAtStart}
          className="text-xs gap-1 h-8"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          {t('flowTester.prevStep')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          className="text-xs gap-1 h-8 text-amber-700 hover:text-amber-800"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          {t('common.reset')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onNextStep}
          disabled={isAtEnd}
          className="text-xs gap-1 h-8"
        >
          {t('flowTester.nextStep')}
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Input area */}
      <div className="flex items-center gap-2 px-3 py-2.5 bg-[#f0f0f0] rounded-b-xl">
        <Input
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            isAtEnd ? t('flowTester.flowComplete') : t('flowTester.typeResponse')
          }
          disabled={isAtEnd}
          className="flex-1 rounded-full bg-white border-0 text-sm h-9 px-4"
        />
        <Button
          size="sm"
          onClick={handleSend}
          disabled={isAtEnd || !inputText.trim()}
          className="rounded-full h-9 w-9 p-0 bg-[#075e54] hover:bg-[#064e46]"
        >
          <Send className="h-4 w-4 text-white" />
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// NodeInspector
// ---------------------------------------------------------------------------

function NodeInspector({ node, locale }: { node: FlowNode | null; locale: 'en' | 'pt-BR' }) {
  const { t } = useTranslation();

  if (!node) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
        <Info className="h-6 w-6 mb-2 opacity-50" />
        <p className="text-sm">{t('flowTester.clickNodeToInspect')}</p>
      </div>
    );
  }

  const color = NODE_TYPE_COLORS[node.nodeType] || 'bg-gray-400';

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center text-white`}>
          <NodeTypeIcon nodeType={node.nodeType} className="h-5 w-5" />
        </div>
        <div>
          <h4 className="font-semibold text-foreground">
            {locale === 'pt-BR' && node.namePtBr ? node.namePtBr : node.name}
          </h4>
          <div className="flex items-center gap-2 mt-0.5">
            <Badge variant="outline" className="text-[10px] capitalize">{node.nodeType}</Badge>
            <span className="text-xs text-muted-foreground">
              {t('flowTester.position')}: {node.position}
            </span>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {/* EN message */}
        <div className="p-3 rounded-lg bg-muted/30">
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">EN</span>
          </div>
          <p className="text-sm text-foreground">
            {node.messageText || (
              <span className="text-muted-foreground italic">{t('flowTester.noMessage')}</span>
            )}
          </p>
        </div>

        {/* PT-BR message */}
        <div className="p-3 rounded-lg bg-muted/30">
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">PT-BR</span>
          </div>
          <p className="text-sm text-foreground">
            {node.messageTextPtBr || (
              <span className="text-muted-foreground italic">{t('flowTester.noMessage')}</span>
            )}
          </p>
        </div>

        {/* Slug */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="font-medium">{t('flowTester.slug')}:</span>
          <code className="px-1.5 py-0.5 rounded bg-muted font-mono text-[11px]">{node.slug}</code>
        </div>

        {/* Linked Template */}
        {node.templateSlug && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="font-medium">{t('flowTester.linkedTemplate', 'Linked Template')}:</span>
            <code className="px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 font-mono text-[11px]">
              {node.templateSlug}
            </code>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TestLog
// ---------------------------------------------------------------------------

function TestLog({ entries }: { entries: SimLogEntry[] }) {
  const { t } = useTranslation();
  const logEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [entries]);

  if (entries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
        <ScrollText className="h-6 w-6 mb-2 opacity-50" />
        <p className="text-sm">{t('flowTester.noLogEntries')}</p>
      </div>
    );
  }

  return (
    <div className="max-h-48 overflow-y-auto space-y-1 font-mono text-xs">
      {entries.map((entry, idx) => (
        <div key={idx} className="flex items-start gap-2 py-1 px-2 rounded hover:bg-muted/30">
          <span className="text-muted-foreground flex-shrink-0 w-[68px]">
            {formatTime(entry.timestamp)}
          </span>
          <span className="flex-shrink-0">
            {entry.type === 'bot' && <Bot className="h-3.5 w-3.5 text-blue-500 inline" />}
            {entry.type === 'user' && <User className="h-3.5 w-3.5 text-emerald-500 inline" />}
            {entry.type === 'system' && <Info className="h-3.5 w-3.5 text-amber-500 inline" />}
          </span>
          <span className={`flex-1 ${entry.type === 'system' ? 'text-amber-700' : 'text-foreground'}`}>
            {entry.nodeName && (
              <span className="text-muted-foreground">[{entry.nodeName}] </span>
            )}
            {entry.message}
          </span>
        </div>
      ))}
      <div ref={logEndRef} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// FunnelAnalytics
// ---------------------------------------------------------------------------

function FunnelAnalytics({
  funnel,
  isLoading,
}: {
  funnel: FunnelStage[];
  isLoading: boolean;
}) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  if (funnel.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
        <BarChart3 className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">{t('flowBuilder.funnel.noData')}</p>
      </div>
    );
  }

  const maxCount = Math.max(...funnel.map((s) => s.count), 1);

  return (
    <div className="space-y-3">
      {funnel.map((stage, idx) => {
        const barWidth = Math.max((stage.count / maxCount) * 100, 4);
        const isLast = idx === funnel.length - 1;

        return (
          <div key={stage.stage}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-foreground">{stage.displayName}</span>
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-foreground">{stage.count}</span>
                <span className="text-xs text-muted-foreground w-12 text-right">
                  {stage.percentage.toFixed(0)}%
                </span>
              </div>
            </div>
            <div className="w-full h-6 bg-muted/30 rounded-md overflow-hidden">
              <div
                className="h-full rounded-md bg-gradient-to-r from-[hsl(160,60%,18%)] to-[hsl(160,84%,39%)] transition-all duration-500"
                style={{ width: `${barWidth}%` }}
              />
            </div>
            {!isLast && stage.dropOff > 0 && (
              <div className="flex items-center gap-1 mt-1 ml-2">
                <ArrowDown className="h-3 w-3 text-rose-400" />
                <span className="text-[11px] text-rose-500">
                  -{stage.dropOff} ({((stage.dropOff / (stage.count + stage.dropOff)) * 100).toFixed(1)}%{' '}
                  {t('flowTester.dropOff')})
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FlowTestTab (main export)
// ---------------------------------------------------------------------------

export function FlowTestTab({ flowSlug, businessId }: FlowTabProps) {
  const { t } = useTranslation();
  const { selectedBusiness } = useOperator();

  // --- Data state ---
  const [flowDetail, setFlowDetail] = useState<FlowDetail | null>(null);
  const [flowDetailLoading, setFlowDetailLoading] = useState(false);
  const [funnelData, setFunnelData] = useState<FunnelStage[]>([]);
  const [funnelLoading, setFunnelLoading] = useState(false);

  // --- UI state ---
  const [locale, setLocale] = useState<'en' | 'pt-BR'>('en');
  const [inspectedNode, setInspectedNode] = useState<FlowNode | null>(null);

  // --- Simulation state ---
  const [currentNodeIndex, setCurrentNodeIndex] = useState(0);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [simLog, setSimLog] = useState<SimLogEntry[]>([]);

  // Sorted nodes by position
  const sortedNodes = useMemo(() => {
    if (!flowDetail?.nodes) return [];
    return [...flowDetail.nodes].sort((a, b) => a.position - b.position);
  }, [flowDetail]);

  const currentNode = sortedNodes[currentNodeIndex] || null;
  const isAtEnd = currentNodeIndex >= sortedNodes.length - 1 && sortedNodes.length > 0;
  const isAtStart = currentNodeIndex === 0;
  const businessName = selectedBusiness?.name || t('flowBuilder.businessNames.barbershop');

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  const resetSimulation = useCallback(() => {
    setCurrentNodeIndex(0);
    setChatMessages([]);
    setSimLog([]);
    setInspectedNode(null);
  }, []);

  // Fetch flow detail
  useEffect(() => {
    if (!flowSlug || !businessId) {
      setFlowDetail(null);
      return;
    }
    let cancelled = false;
    setFlowDetailLoading(true);

    getFlow(flowSlug, businessId)
      .then((data) => {
        if (!cancelled) {
          setFlowDetail(data);
          resetSimulation();
        }
      })
      .catch(() => {
        if (!cancelled) setFlowDetail(null);
      })
      .finally(() => {
        if (!cancelled) setFlowDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [flowSlug, businessId, resetSimulation]);

  // Fetch funnel data
  useEffect(() => {
    if (!flowSlug || !businessId) {
      setFunnelData([]);
      return;
    }
    let cancelled = false;
    setFunnelLoading(true);

    getFlowFunnel(flowSlug, businessId)
      .then((data) => {
        if (!cancelled) setFunnelData(data.funnel);
      })
      .catch(() => {
        if (!cancelled) setFunnelData([]);
      })
      .finally(() => {
        if (!cancelled) setFunnelLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [flowSlug, businessId]);

  // -------------------------------------------------------------------------
  // Simulation engine
  // -------------------------------------------------------------------------

  const pushBotMessage = useCallback((node: FlowNode, loc: 'en' | 'pt-BR') => {
    const text =
      loc === 'pt-BR' && node.messageTextPtBr ? node.messageTextPtBr : node.messageText;
    const name = loc === 'pt-BR' && node.namePtBr ? node.namePtBr : node.name;

    if (text) {
      const now = new Date();
      setChatMessages((prev) => [
        ...prev,
        {
          id: `bot-${Date.now()}-${Math.random()}`,
          sender: 'bot',
          text,
          timestamp: now,
          nodeSlug: node.slug,
        },
      ]);
      setSimLog((prev) => [
        ...prev,
        {
          timestamp: now,
          type: 'bot',
          nodeSlug: node.slug,
          nodeName: name,
          message: text,
        },
      ]);
    }
  }, []);

  const pushSystemLog = useCallback(
    (message: string, nodeSlug?: string, nodeName?: string) => {
      setSimLog((prev) => [
        ...prev,
        {
          timestamp: new Date(),
          type: 'system',
          nodeSlug: nodeSlug || null,
          nodeName: nodeName || null,
          message,
        },
      ]);
    },
    [],
  );

  // Auto-push bot message when a node becomes active
  useEffect(() => {
    if (sortedNodes.length > 0 && currentNode) {
      const nodeName =
        locale === 'pt-BR' && currentNode.namePtBr ? currentNode.namePtBr : currentNode.name;
      pushSystemLog(
        `${t('flowTester.enteredNode')}: ${nodeName}`,
        currentNode.slug,
        nodeName,
      );
      pushBotMessage(currentNode, locale);
    }
    // Intentionally depend on index + length for init
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNodeIndex, sortedNodes.length]);

  const handleNextStep = useCallback(() => {
    if (isAtEnd) return;
    setCurrentNodeIndex((prev) => prev + 1);
  }, [isAtEnd]);

  const handlePrevStep = useCallback(() => {
    if (isAtStart) return;
    setCurrentNodeIndex((prev) => prev - 1);
  }, [isAtStart]);

  const handleSendMessage = useCallback(
    (text: string) => {
      const now = new Date();
      setChatMessages((prev) => [
        ...prev,
        {
          id: `user-${Date.now()}-${Math.random()}`,
          sender: 'user',
          text,
          timestamp: now,
        },
      ]);
      setSimLog((prev) => [
        ...prev,
        {
          timestamp: now,
          type: 'user',
          nodeSlug: currentNode?.slug || null,
          nodeName: null,
          message: text,
        },
      ]);

      // Auto-advance to next node after user input
      if (!isAtEnd) {
        setTimeout(() => {
          setCurrentNodeIndex((prev) => prev + 1);
        }, 400);
      }
    },
    [currentNode, isAtEnd],
  );

  const handleReset = useCallback(() => {
    resetSimulation();
    // Re-trigger initial bot message after reset
    if (sortedNodes.length > 0) {
      setTimeout(() => {
        const firstNode = sortedNodes[0];
        const nodeName =
          locale === 'pt-BR' && firstNode.namePtBr ? firstNode.namePtBr : firstNode.name;
        pushSystemLog(t('flowTester.simulationReset'));
        pushSystemLog(
          `${t('flowTester.enteredNode')}: ${nodeName}`,
          firstNode.slug,
          nodeName,
        );
        pushBotMessage(firstNode, locale);
      }, 100);
    }
  }, [sortedNodes, locale, pushBotMessage, pushSystemLog, resetSimulation, t]);

  const handleNodeClick = useCallback((node: FlowNode) => {
    setInspectedNode((prev) => (prev?.id === node.id ? null : node));
  }, []);

  const toggleLocale = useCallback(() => {
    setLocale((prev) => (prev === 'en' ? 'pt-BR' : 'en'));
  }, []);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="space-y-6" data-testid="flow-test-tab">
      {/* Controls: locale toggle */}
      <div className="flex items-center justify-end gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={toggleLocale}
          className="flex items-center gap-2"
          data-testid="locale-toggle"
        >
          <Globe className="h-4 w-4" />
          {locale === 'en' ? 'EN' : 'PT'}
        </Button>
      </div>

      {flowDetailLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : flowDetail ? (
        <>
          {/* Main grid: Visualizer + Simulator */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Flow Visualizer */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Eye className="h-5 w-5 text-muted-foreground" />
                      {t('flowTester.visualizerTitle')}
                    </CardTitle>
                    <CardDescription>
                      {t('flowTester.visualizerDescription', {
                        current: currentNodeIndex + 1,
                        total: sortedNodes.length,
                      })}
                    </CardDescription>
                  </div>
                  {isAtEnd && (
                    <Badge variant="completed" className="gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      {t('flowTester.flowComplete')}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-[500px] overflow-y-auto border-t px-2">
                  <FlowVisualizer
                    nodes={sortedNodes}
                    currentIndex={currentNodeIndex}
                    inspectedNode={inspectedNode}
                    locale={locale}
                    onNodeClick={handleNodeClick}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Right sidebar: Simulator + Inspector */}
            <div className="space-y-4">
              {/* Chat Simulator */}
              <Card className="overflow-hidden">
                <ChatSimulator
                  messages={chatMessages}
                  onSendMessage={handleSendMessage}
                  onNextStep={handleNextStep}
                  onPrevStep={handlePrevStep}
                  onReset={handleReset}
                  isAtEnd={isAtEnd}
                  isAtStart={isAtStart}
                  locale={locale}
                  businessName={businessName}
                />
              </Card>

              {/* Node Inspector */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Info className="h-4 w-4 text-muted-foreground" />
                    {t('flowTester.nodeInspector')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <NodeInspector node={inspectedNode} locale={locale} />
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Funnel Analytics */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-muted-foreground" />
                <CardTitle className="text-lg">{t('flowBuilder.funnel.title')}</CardTitle>
              </div>
              <CardDescription>{t('flowBuilder.funnel.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <FunnelAnalytics funnel={funnelData} isLoading={funnelLoading} />
            </CardContent>
          </Card>

          {/* Test Log */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ScrollText className="h-5 w-5 text-muted-foreground" />
                  <CardTitle className="text-lg">{t('flowTester.testLog')}</CardTitle>
                </div>
                <Badge variant="outline" className="text-[10px]">
                  {simLog.length} {t('flowTester.entries')}
                </Badge>
              </div>
              <CardDescription>{t('flowTester.testLogDescription')}</CardDescription>
            </CardHeader>
            <CardContent>
              <TestLog entries={simLog} />
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
